import { QueuePlaybackService } from "../services/QueuePlaybackService";
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonList,
  IonProgressBar,
  IonRow,
} from "@ionic/react";
import { arrowDownOutline, playOutline } from "ionicons/icons";
import React, { useState } from "react";
import { usePlayer } from "../data/store/usePlayer";
import { usePlaylist } from "../data/store/usePlaylist";
import { songHookStatus, useSongs } from "../data/store/useSongs";
import { SongRef } from "../models/Song";
import MediaService from "../services/MediaService";
import CompactSongItem from "./CompactSongItem";
import Spacer from "./Spacer";

interface OwnProps {
  songRefs: SongRef[];
  fetchMore: () => void;
  status: songHookStatus;
  compact?: boolean;
  withLimit?: boolean;
  nextPageText?: string;
  hideFetchButton?:boolean;
  // When provided, "Play All" enables continuous (YouTube-style) autoplay: it
  // queues the visible page and loads more as it runs out, instead of pre-queuing
  // an entire feed/catalog. Returns the newly-loaded ids.
  loadMore?: () => Promise<string[]>;
}

interface SongListProps extends OwnProps {}

const CompactSongList: React.FC<SongListProps> = ({
  songRefs,
  status,
  fetchMore,
  nextPageText,
  withLimit = true,
  loadMore,
hideFetchButton=false
}) => {
  const parentRef = React.useRef<HTMLIonListElement>(null);
  const { queueSong, setNowPlaying, setLoadMore } = usePlaylist();
  const isPaused = usePlayer((x) => x.isPaused);
  const retrieve = useSongs((x) => x.retrieve);
  const [limit, setLimit] = useState<number>(20);

  const handleFetchMore = () => {
    if (withLimit) {
      songRefs.length > limit ? setLimit((x) => x + 20) : fetchMore();
    } else {
      fetchMore();
    }
  };

  const playAll = () => {
    // Register continuous autoplay (feed/artist) or clear it (finite lists).
    setLoadMore(loadMore ?? null);
    if (songRefs.length === 0) return;
    songRefs.forEach((x) => queueSong(x.id));
    // Start at the first song this viewer can actually play (later entries
    // may not be loaded yet, or be members-only for them).
    if (isPaused) QueuePlaybackService.playFirst(songRefs.map((x) => x.id));
  };

  return (
    <IonGrid style={{margin:0}}>
      <IonList className="ion-no-margin" ref={parentRef} style={{ background: "clear", margin: 0, marginTop: 8 }} color={"light"}>
      {songRefs.map((songRef)=><CompactSongItem key={songRef.id} id={songRef.id} />)}
      </IonList>
      {songRefs.length !== 0 && (
        <IonRow>
          {/* Full width and centered when there's no Load More to share the
              row with — matches the feed's top Play All button — rather
              than staying stuck at half width with an empty gap beside it. */}
          <IonCol size={hideFetchButton ? "12" : "6"}>
            <IonButton
              fill="outline"
              color="favorite"
              expand="full"
              onClick={playAll}
              style={{
                // Plain `padding` here is real CSS padding on ion-button's
                // host, which shrinks the button's own border box inward on
                // every side (confirmed: the visible border sat 5px inside
                // the host's true edge) — that's what made this button read
                // as narrower than the compact-toggle row above despite
                // their outer boxes being pixel-aligned. The --padding-*
                // custom properties pad the button's *content* instead,
                // without moving its border box.
                "--padding-top": "5px",
                "--padding-bottom": "5px",
                "--padding-start": "5px",
                "--padding-end": "5px",
                borderRadius: "8px",
              } as React.CSSProperties}
            >
              Play All
              <IonIcon icon={playOutline} />
            </IonButton>
          </IonCol>
          {hideFetchButton===false&&<IonCol size="6">
            {status === "idle" ? (
              <IonButton
                fill="outline"
                color="tertiary"
                expand="full"
                onClick={handleFetchMore}
                style={{
                  "--padding-top": "5px",
                  "--padding-bottom": "5px",
                  "--padding-start": "5px",
                  "--padding-end": "5px",
                  borderRadius: "8px",
                } as React.CSSProperties}
              >
                {nextPageText || "Load More "}
                <IonIcon icon={arrowDownOutline} />
              </IonButton>
            ) : (
              <IonProgressBar
                style={{ height: 52 }}
                type="indeterminate"
                color="tertiary"
              />
            )}
          </IonCol>}
          <Spacer height={100}/>
        </IonRow>
      )}
    </IonGrid>
  );
};

export default CompactSongList;
